import { useEffect, useMemo, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";
import { armarMailRadar } from "../../constants/plantillasRadar";
import { MESES_ES } from "../../utils/alertasRecurrencia";
import { registrarBorradorRadar } from "../../utils/radarBorradores";
import styles from "./stylesEscribirRadarModal.module.scss";

// Modal "Escribir" del Radar Comercial (Fase B) —
// docs/decisiones/2026-09-26-radar-mail-pagina-diseno.md §3, §9. Molde estándar del proyecto
// (`reactstrap` Modal/ModalHeader/ModalBody/ModalFooter, mismo patrón que `AgendarMovimientoModal`).
//
// SIN "agregar contacto" en esta fase (decisión explícita del prompt de Fase B): la lista de
// contactos es de solo selección (checkbox) sobre lo que ya trae `radarContactos.js` — sumar
// contactos nuevos y ofrecer registrarlos queda para el proyecto de Contactos aparte (§9 del
// diseño documenta por qué: el CRM hoy reescribe la tabla relacional completa en cada guardado,
// tocar eso de paso sería un riesgo mayor al que pide esta tarea).
//
// Abrir el borrador (botón "Abrir en Gmail") NUNCA cambia el estado de la alerta — `escrita` solo
// llega, más tarde, cuando `gmail-sync` confirma un mail SALIENTE real (`radarMailSaliente.js`).
// El insert en `radar_borradores` de acá es puramente informativo (trazabilidad de intención) y
// nunca bloquea el envío si falla (10.170, ver `registrarBorradorRadar`).
//
// MODO_APERTURA_GMAIL (2026-09-26) — Ignacio verificó en su Gmail real que `?view=cm&...`
// (redacción "en línea") NO trae la firma predeterminada de la cuenta ni el estilo de texto
// predeterminado (Trebuchet en su caso) — Redactar sí los trae. Se prueba la otra vía: un
// `mailto:` armado a mano, abierto vía `https://mail.google.com/mail/?extsrc=mailto&url=...`
// (el mismo mecanismo que usa Gmail para los links `mailto:` de una página web), que abre el
// redactor NORMAL de la bandeja — con firma y tipografía de la cuenta. Constante única para poder
// volver a "cm" en un solo lugar si "mailto" no sirviera. Ver ficha del módulo para el resultado
// de la prueba de Ignacio.
const MODO_APERTURA_GMAIL = "mailto"; // "mailto" (nuevo, default) | "cm" (el de antes)

const construirUrlGmailMailto = ({ to, asunto, cuerpo }) => {
  const mailtoUri = `mailto:${to}?subject=${encodeURIComponent(asunto)}&body=${encodeURIComponent(cuerpo)}`;
  return `https://mail.google.com/mail/?extsrc=mailto&url=${encodeURIComponent(mailtoUri)}`;
};

const construirUrlGmailCompose = ({ to, asunto, cuerpo, usuarioEmail }) =>
  "https://mail.google.com/mail/?view=cm&fs=1" +
  `&to=${encodeURIComponent(to)}` +
  `&su=${encodeURIComponent(asunto)}` +
  `&body=${encodeURIComponent(cuerpo)}` +
  // `authuser` (mecanismo documentado de Gmail para elegir la cuenta cuando hay varias abiertas en
  // el mismo navegador): no se pudo verificar en un browser autenticado real (10.28). `?view=cm`
  // no soporta un análogo directo de todos modos — queda documentado acá, no en la vía nueva.
  (usuarioEmail ? `&authuser=${encodeURIComponent(usuarioEmail)}` : "");

export const EscribirRadarModal = ({
  isOpen,
  onClose,
  lado, // 'cliente' | 'agencia'
  alerta, // fila del Radar: empresaDisplay, empresaNorm, mesObjetivo, anoObjetivo, tipo, campana,
  // edificiosTotales, clientId
  contactos, // [{usuarioId, nombre, apellido, email, cargo}] — ya filtrados a con-email
  edificiosOperativos, // M de hoy (getEdificiosOperativos, ver banner) — null si no cargó/no confía
  pais,
  usuarioId,
  usuarioNombre,
  usuarioEmail,
}) => {
  const [seleccionados, setSeleccionados] = useState(() => new Set());

  // Al abrir, todos marcados por defecto (§3.2: "todos los contactos del lado van en Para").
  useEffect(() => {
    if (isOpen) setSeleccionados(new Set(contactos.map((c) => c.usuarioId)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, alerta?.clientId, lado]);

  const mes = MESES_ES[alerta?.mesObjetivo] || "";

  // Greeting fijo al PRIMER contacto de la lista (no al primer marcado): la vista previa no
  // recalcula al tildar/destildar checkboxes — es un borrador, el vendedor ajusta el saludo en
  // Gmail si hace falta antes de mandar.
  const { asunto, cuerpo } = useMemo(
    () =>
      armarMailRadar({
        lado,
        tipo: alerta?.tipo,
        empresa: alerta?.empresaDisplay,
        campana: alerta?.campana,
        mes,
        nEntonces: alerta?.edificiosTotales ?? null,
        nHoy: edificiosOperativos ?? null,
        firma: usuarioNombre,
        pais,
        nombreContacto: contactos[0]?.nombre,
      }),
    [lado, alerta, mes, edificiosOperativos, usuarioNombre, pais, contactos]
  );

  const toggleContacto = (usuarioId2) => {
    setSeleccionados((prev) => {
      const next = new Set(prev);
      if (next.has(usuarioId2)) next.delete(usuarioId2);
      else next.add(usuarioId2);
      return next;
    });
  };

  const abrirEnGmail = () => {
    const marcados = contactos.filter((c) => seleccionados.has(c.usuarioId));
    if (marcados.length === 0) return;

    registrarBorradorRadar({
      pais,
      empresaNorm: alerta.empresaNorm,
      mes: alerta.mesObjetivo,
      ano: alerta.anoObjetivo,
      lado,
      clientId: alerta.clientId,
      usuarioId,
    });

    const to = marcados.map((c) => c.email).join(",");
    const url =
      MODO_APERTURA_GMAIL === "mailto"
        ? construirUrlGmailMailto({ to, asunto, cuerpo })
        : construirUrlGmailCompose({ to, asunto, cuerpo, usuarioEmail });
    window.open(url, "_blank", "noopener,noreferrer");
    onClose();
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered className={styles.dialog}>
      <ModalHeader toggle={onClose}>
        Escribir a {alerta?.empresaDisplay} · {lado === "agencia" ? "Agencia" : "Cliente"}
      </ModalHeader>
      <ModalBody>
        <div className={styles.contactos}>
          {contactos.map((c) => (
            <label key={c.usuarioId} className={styles.contacto}>
              <input
                type="checkbox"
                checked={seleccionados.has(c.usuarioId)}
                onChange={() => toggleContacto(c.usuarioId)}
              />
              <span className={styles.contactoNombre}>
                {c.nombre} {c.apellido}
              </span>
              {c.cargo && <span className={styles.contactoCargo}>{c.cargo}</span>}
              <span className={styles.contactoEmail}>{c.email}</span>
            </label>
          ))}
        </div>
        <div className={styles.preview}>
          <div className={styles.previewAsunto}>
            <strong>Asunto:</strong> {asunto}
          </div>
          <div className={styles.previewCuerpo}>{cuerpo}</div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={onClose}>
          Cancelar
        </Button>
        <Button color="primary" onClick={abrirEnGmail} disabled={seleccionados.size === 0}>
          Abrir en Gmail
        </Button>
      </ModalFooter>
    </Modal>
  );
};
